/**
 * Checkbox Component
 */

import { v4 as uuidv4 } from 'uuid'

import { IBuilderComponent } from '../../base'
import { formatFromVariableSelect, formatFromVariableText } from '../../format-utils'
import type { VariableSelectValue, VariableTextValue } from '../../types'
import { deepMerge, parseToVariableSelect, parseToVariableText } from '../../parser-utils'
import checkboxTemplate from './checkbox.template.json'

export interface ICheckboxComponentSettings {
  // Data settings
  'checkbox-data-value': string // Column ID
  'checkbox-data-label': VariableTextValue
  'checkbox-data-default-value': VariableSelectValue
  'checkbox-data-default-value-editable': boolean
  // Behavior settings
  'checkbox-behavior-required'?: boolean
}

export interface ICheckboxComponent extends IBuilderComponent {
  componentId: 'checkbox'
  groupId: 'form'
  settings: ICheckboxComponentSettings
}

/**
 * Checkbox AI Input
 */
export interface AiCheckboxInput {
  columnId: string // Required: maps to table column
  label?: string[] // Format: ["text:Checkbox Label"] or ["text:Accept ", "cr:terms"]
  defaultValue?: boolean // Default value
  required?: boolean // Is this field required?
}

const CHECKBOX_DEFAULT_TEMPLATE = checkboxTemplate as unknown as ICheckboxComponentSettings

export class CheckboxComp {
  getTemplate(): ICheckboxComponentSettings {
    return CHECKBOX_DEFAULT_TEMPLATE
  }

  /**
   * format checkbox component settings to AI checkbox input
   */
  format(settings: ICheckboxComponentSettings): AiCheckboxInput {
    const result: AiCheckboxInput = {
      columnId: settings['checkbox-data-value'],
    }

    // Label
    const label = formatFromVariableText(settings['checkbox-data-label'])
    if (label && label.length > 0) {
      result.label = label
    }

    // Default Value (convert from VariableSelectValue to boolean)
    const defaultValue = formatFromVariableSelect(settings['checkbox-data-default-value'])
    if (defaultValue) {
      // Parse the defaultValue string (e.g., "text:true" or "text:false")
      if (defaultValue.startsWith('text:')) {
        const value = defaultValue.replace('text:', '')
        result.defaultValue = value === 'true'
      }
    }

    // Required
    if (settings['checkbox-behavior-required'] !== undefined) {
      result.required = settings['checkbox-behavior-required']
    }

    return result
  }

  /**
   * parse AI checkbox input to checkbox component settings
   */
  parse(input: AiCheckboxInput): ICheckboxComponentSettings {
    const result = deepMerge(CHECKBOX_DEFAULT_TEMPLATE, {})

    // Data - Column ID (required for form fields)
    if (input.columnId) {
      result['checkbox-data-value'] = input.columnId
    }

    // Data - Label
    if (input.label) {
      result['checkbox-data-label'] = parseToVariableText(input.label)
    }

    // Data - Default Value
    if (input.defaultValue !== undefined) {
      result['checkbox-data-default-value'] = {
        flag: 2048, // CustomText flag
        id: uuidv4(),
        value: input.defaultValue ? 'true' : 'false',
      }
    }

    return result
  }
}

